import { getMonthFromDate } from "../extensions/date";

export interface RajasthanDiscomDrawal {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  month: string;
  block: number; // Positive integer representing the block
  area: string; // Area as a string (up to 100 characters)
  mw: number; // MW value as a float
  mwh: number; // MW value as a float
  discom: string; // Discom as a string (up to 50 characters)
  district: string;
}

export interface RajasthanDiscomData {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  month: string;
  block: number; // Positive integer representing the block
  discom: string; // Discom as a string (up to 50 characters)
  schedule_mw: number; // MW value as a float
  drawal_mw: number; // MW value as a float
  drawal_mwh: number; // MW value as a float
  load_shedding_mw: number;
  total_mw: number;
  dates: string;
  forcast_type?: string | number; // Optional forecast type identifier
}

export interface HarayanaDiscomData {
  demand_mw: any;
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  month: string;
  slot: number; // Positive integer representing the block
  block: number; // Positive integer representing the block
  demand_value_mw: number;
  dates: string;
  time_string: string;
}

export interface HarayanaWeatherData {
  id: number;
  date: string;
  dates: string;
  location: string;
  weather: string;
  wind_km_per_hr: string;
  visibility_km: string;
  temperature_c: number;
  humidity_percent: number;
  barometer_mbar: string;
  slot: number;
}

export interface HarayanaDailyPeakData {
  date: string;
  daily_peak_demand_value_mw: number;
  avg_daily_peak_demand_value_mw: string;
  formatted_data: [
    {
      time_period: string;
      max_demand: number;
      min_demand: number;
      max_demand_slot: number;
      min_demand_slot: number;
    },
    {
      time_period: string;
      max_demand: number;
      min_demand: number;
      max_demand_slot: number;
      min_demand_slot: number;
    }
  ];
}
export interface DailyDiscomDrawalSchedule {
  date: string;
  discom?: string;

  // Daily peak values
  daily_peak_drawal_mw: number;
  daily_peak_schedule_mw: number;

  // Daily average values
  avg_daily_drawal_mw: number;
  avg_daily_schedule_mw: number;

  // Evening peak values
  evening_peak_drawal_mw: number;
  evening_peak_drawal_slot: number;
  evening_peak_schedule_mw: number;
  evening_peak_schedule_slot: number;

  // Morning peak values
  morning_peak_drawal_mw: number;
  morning_peak_drawal_slot: number;
  morning_peak_schedule_mw: number;
  morning_peak_schedule_slot: number;

  // Meta information
  dateIndex?: string;
  dates?: string;
  month?: string;

  // Formatted data for both time periods
  formatted_data: [
    {
      time_period: string;
      max_schedule: number;
      max_drawal: number;
      min_schedule: number;
      min_drawal: number;
      avg_schedule: number;
      avg_drawal: number;
      max_schedule_slot: number;
      max_drawal_slot: number;
      min_schedule_slot: number;
      min_drawal_slot: number;
    },
    {
      time_period: string;
      max_schedule: number;
      max_drawal: number;
      min_schedule: number;
      min_drawal: number;
      avg_schedule: number;
      avg_drawal: number;
      max_schedule_slot: number;
      max_drawal_slot: number;
      min_schedule_slot: number;
      min_drawal_slot: number;
    }
  ];
}

export interface HarayanaDailyForecastData {
  avg_daily_peak_demand_value_mw: any;
  daily_peak_demand_value_mw: any;
  date: string;
  daily_demand_value_mw: number;
  avg_demand_value_mw: string;
  demand_mw: number;
  average_demand_mw: number;
}

export interface HarayanaMonthlyPeakData {
  month: number | string;
  year: number;
  monthly_peak_demand_value_mw: number;
  avg_monthly_peak_demand_value_mw: string;
  month_year: string;
  peak_mw: number;
  average_mw: number;
}

export interface HarayanaMonthlyForecastData {
  peak_mw: number;
  average_mw: number;
  month: number | string;
  year: number;
  monthly_demand_value_mw: number;
  avg_demand_value_mw: string;
  month_year: string;
}

export interface RajasthanGenData {
  date: string; // Assuming date is returned as a string in 'yyyy-mm-dd' format
  month: string;
  slot: number; // Assuming slot can be a string (time range) or a number
  station: string;
  source: string;
  state: string;
  sector?: string;
  onBarDS_mw: number | null;
  onBarDc_mw?: number | null;
  value_mw?: number;
  value_mwh?: number;
  backdown_mw?: number | null;
  onBarDS_mwh: number | null;
  onBarDc_mwh?: number | null;
  backdown_mwh?: number | null;
}

interface IdName {
  [id: number]: string;
}
export interface RajMappings {
  generation_sources: IdName;
  utilities: IdName;
  ir_links: IdName;
  states: IdName;
}

export function convertToRajasthanGenData(params: any[]): RajasthanGenData {
  // <QuerySet [(1, "['Adanikawai', 'BarsingsarNeyveli', 'CSCTPP', 'ChhabraTpsStage1Unit1234', 'DholpurGtppUnit123', 'GIR", "['Adanikawai', 'BarsingsarNeyveli', 'CSCTPP', 'ChhabraTpsStage1Unit1234', 'DholpurGtppUnit123', 'GIR"), (2, 'Adanikawai', 'Adanikawai'), (3, 'BarsingsarNeyveli', 'BarsingsarNeyveli'), (4, 'CSCTPP', 'CSCTPP'), (5, 'ChhabraTpsStage1Unit1234', 'ChhabraTpsStage1Unit1234'), (6, 'DholpurGtppUnit123', 'DholpurGtppUnit123'), (7, 'GIRAL', 'GIRAL'), (8, 'JSHYDRO', 'JSHYDRO'), (9, 'KTPS', 'KTPS'), (10, 'KalisindhTpsStage12', 'KalisindhTpsStage12'), (11, 'MAHI', 'MAHI'), (12, 'RAPPA', 'RAPPA'), (13, 'RGTPP', 'RGTPP'), (14, 'RPS', 'RPS'), (15, 'Rajwest', 'Rajwest'), (16, 'SSCTPS_8', 'SSCTPS_8'), (17, 'STPS', 'STPS'), (18, 'VSLIGNITE', 'VSLIGNITE'), (19, 'SSCTPS_7', 'SSCTPS_7')]>

  const stations = {
    Adanikawai: "Adani Kawai",
    BarsingsarNeyveli: "Barsingsar Neyveli",
    CSCTPP: "Chhabra TPS",
    ChhabraTpsStage1Unit1234: "Chhabra TPS",
    DholpurGtppUnit123: "Dholpur GTPP",
    GIRAL: "Giral",
    JSHYDRO: "Jawahar Sagar Hydro",
    KTPS: "Kota TPS",
    KalisindhTpsStage12: "Kalisindh TPS",
    MAHI: "Mahi",
    RAPPA: "RAPP",
    RGTPP: "Rajasthan GTPP",
    RPS: "Rajasthan Power Station",
    Rajwest: "Rajwest",
    SSCTPS_8: "Suratgarh TPS",
    STPS: "Sikar TPS",
    VSLIGNITE: "VSLignite",
    SSCTPS_7: "Suratgarh TPS",
  };
  const station_mapping: {
    [key: number]: string;
  } = {
    1: "Adanikawai",
    2: "BarsingsarNeyveli",
    3: "CSCTPP",
    4: "ChhabraTpsStage1Unit1234",
    5: "DholpurGtppUnit123",
    6: "GIRAL",
    7: "JSHYDRO Hydro RE",
    8: "KTPS",
    9: "KalisindhTpsStage12",
    10: "MAHI Hydro",
    11: "RAPPA",
    12: "RGTPP",
    13: "RPS Hydro",
    14: "Rajwest",
    15: "SSCTPS_8",
    16: "STPS",
    17: "VSLIGNITE",
    18: "SSCTPS_7",
    19: "SSCTPS_7",
  };
  //  'onBarDS', 'onBarDc', 'backdown'
  return {
    date: params[0],
    month: getMonthFromDate(params[0]),
    state: "Rajasthan",
    source: params[6],
    slot: params[1],
    station: params[2],
    onBarDS_mw: params[3],
    value_mw: params[3],
    value_mwh: params[3] / 4,
    onBarDc_mw: params[4],
    backdown_mw: params[5],
    onBarDS_mwh: params[3] / 4,
    onBarDc_mwh: params[4] / 4,
    backdown_mwh: params[5] / 4,
  };
}

export interface RajasthanGnaTgna {
  buy_sell: string;
  type__name: any;
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  value_mw: number; // Value in megawatts (MW)
  trader__name: string; // Name of the trader
  from_state__name: string; // Name of the origin state
  from_utility__entity_state__name: string; // Name of the origin utility
  to_state__name: string; // Name of the destination state
  from_utility__name: string; // Name of the origin utility
  to_utility__name: string; // Name of the destination utility
  ir_link__name: string; // Name of the inter-regional link
  category__category: string; // Category name
  time_block: number; // Time block number (integer)
  generator_source: string; // Source of the generator
}

export interface RajasthanCollective {
  time_block: any;
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  // rev: number; // Revision number
  month: string;
  cost: number; // Cost value
  wt_mcp_rs_kwh: number; // Weighted MCP value
  value_mw: number; // MW value
  product__name: string; // Name of the product
  exchange__name: string; // Name of the exchange
  time_slot: number; // Time slot (nullable)
  sheet: number; // Sheet information (nullable)
}

export interface RajasthanIsgs {
  fiscal_year: any;
  quarter: any;
  year: undefined;
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  month: string;
  slot: number; // Time block number
  from_utility__name: string; // Name of the origin utility
  from_gen__generation_source: string;
  state: string; // Name of the origin state
  station: string;
  ir_link__name: string; // Name of the IR link
  value_mw: number; // MW value
  value_mwh: number; // MW value
  generator_source: string; // Source of the generator
}

export interface HaryanaIsgs {
  id: number;
  date: string;
  region: string;
  category: string;
  profile_type: string;
  boundary_type: string;
  trader: string;
  appr_no: string;
  from_region: string;
  from_parent_discom_utility: string;
  from_unit: string;
  to_region: string;
  to_parent_discom_utility: string;
  to_unit: string;
  generator_type: string | null;
  value_mw: number;
  value_mwh: number;
  cassandra_id: string;
  slot: number;
  from_state: number | null;
  from_utility: number | null;
  to_state: number;
  to_utility: number;
  ir_link: number;
}

export interface HaryanaGnaTgna {
  boundary_type__boundary_type: string;
  id: number;
  region: string;
  category: string;
  profile_type: string;
  boundary_type: string;
  trader: string;
  appr_no: string;
  from_region: string;
  from_parent_discom_utility: string;
  from_unit: string;
  to_region: string;
  to_parent_discom_utility: string;
  to_unit: string;
  value_mwh: number;
  cassandra_id: string;
  slot: number;
  from_state: number | null;
  from_utility: number | null;
  to_state: number;
  to_utility: number;
  ir_link: number;
  buy_sell: string;
  type__name: any;
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  value_mw: number; // Value in megawatts (MW)
  trader__name: string; // Name of the trader
  from_state__name: string; // Name of the origin state
  from_utility__entity_state__name: string; // Name of the origin utility
  to_state__name: string; // Name of the destination state
  from_utility__name: string; // Name of the origin utility
  to_utility__name: string; // Name of the destination utility
  ir_link__name: string; // Name of the inter-regional link
  category__category: string; // Category name
  time_block: number; // Time block number (integer)
  generator_source: string; // Source of the generator
  time_string: string;
}

export interface HaryanaRemc {
  id: number;
  date: string;
  region: string;
  category: string;
  profile_type: string;
  boundary_type: string;
  trader: string;
  appr_no: string;
  from_region: string;
  from_parent_discom_utility: string;
  from_unit: string;
  to_region: string;
  to_parent_discom_utility: string;
  to_unit: string;
  value_mw: number;
  value_mwh: number;
  cassandra_id: string;
  slot: number;
  from_state: null | number;
  from_utility: null | number;
  to_state: number;
  to_utility: number;
  ir_link: number;
}

export interface CompareRajasthan {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  dates?: string;
  month: string;
  entity?: string; // Name of the entity
  toEntity?: string; // Name of the selling Entity
  time_block: number; // Time block number
  type: string; // Name of the origin utility
  value_mw: number; // MW value
  exchange?: string; // Name of the exchange
  product?: string; // Name of the product
  price_kwh?: number; // Price per KWH
}

export interface RajProcurementRecord {
  id: number;
  type_of_transaction: String;
  purchase_through_product: String;
  platform: String;
  tender_no: String;
  trader_bidder: String;
  application_no: String;
  name_of_counterparty_generator: String;
  drawal_purchase_import_date_from: String;
  drawal_purchase_import_date_to: String;
  drawal_purchase_import_time_from: String;
  drawal_purchase_import_time_to: String;
  drawal_purchase_import_mw: String;
  drawal_purchase_import_lu: String;
  rate_banking_factor: String;
  amount_cr_per_day: String;
  inject_sell_export_date_from: String;
  inject_sell_export_date_to: String;
  inject_sell_export_time_from: String;
  inject_sell_export_time_to: String;
  inject_sell_export_mw: String;
  inject_sell_export_lu: String;
  ppa_date_agreement_date_loi_date: String;
  loi_number_approval_id_agreement_id: String;
}

export interface ReEnergyData {
  // date=row["date"],
  // time_block=row["time_block"],
  // energy_type=row["type"],
  // value_mw=row["value_mw"]
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  month: string;
  area: string;
  time_block: number; // Time block number
  energy_type: string; // Type of energy
  value_mw: number;
  value_mwh: number;
  slot: number; // Time slot number
  energy_type__name: string; // Name of the energy type
}

export interface RajGenParams {
  year: string; // Corresponds to CharField(max_length=20)
  generator: string; // Corresponds to CharField(max_length=255)
  total_capacity_installed_mw?: number | null; // Corresponds to FloatField(null=True, blank=True)
  normative_availability_percent?: number | null; // Corresponds to FloatField(null=True, blank=True)
  availability_percent?: number | null; // Corresponds to FloatField(null=True, blank=True)
  plf_percent?: number | null; // Corresponds to FloatField(null=True, blank=True)
  fix_charges_rs_per_kwh?: number | null; // Corresponds to FloatField(null=True, blank=True)
  variable_charges_rs_per_kwh?: number | null; // Corresponds to FloatField(null=True, blank=True)
  total_tariff_rs_per_kwh?: number | null; // Corresponds to FloatField(null=True, blank=True)
  comment?: string | null; // Corresponds to TextField(null=True, blank=True)
  type: string; // Corresponds to CharField(max_length=50)
  share_of_state_percent?: number | null; // Corresponds to FloatField(null=True, blank=True)
  additional_comments?: string | null; // Corresponds to TextField(null=True, blank=True)
}

export interface RajasthanUploadFile {
  id: number;
  file: string; // The relative path of the file
  file_url: string; // The full URL of the file
  uploaded_at: string; // The timestamp when the file was uploaded
  type: string; // The type of the file (name of the type)
  user_date: string | null; // The user provided date (can be null)
  date: string; // The timestamp when the file record was created
}

export interface RajGenProfile {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  block: number; // Time block number
  intra_state_conventional_generation: number;
  intra_state_renewable_generation: number;
  inter_state_generation: number;
  long_term_agreement_lta: number;
  reverse_auction_deep: number;
  total_generation_procurement: number;
}

export interface RajSurplusDeficitData {
  id: number;
  date: string;
  time_block: number;
  block: number;
  sgu: number;
  biomass: number;
  hydro: number;
  solar: number;
  wind: number;
  isgs: number;
  lta: number;
  ldc: number;
  sgu_outage: number;
  sgu_with_outage: number;
  total: number;
  forecasted: number;
  demand_2024: number;
  surplus_deficit: number;
}

export interface RajProcurement {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  slot: number; // Time block number
  exchange: string;
  entity: string;
  product: string;
  value_mw: number;
  value_mwh: number;
  price_kwh: number;

  // [key: string]: any;
}

// "date": "2024-08-01",
// "block": 1,
// "mw": 200.0,
// "price_kwh": 9528.333333333334,
// "exchange": "PXIL",
// "product": "TAM"

export interface RajTamData {
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  block: number; // Time block number
  mw: number;
  price_kwh: number;
  exchange: string;
  product: string;
}

export interface RajBankingData {
  // class RajBanking(models.Model):
  // date = models.DateField()
  // from_state = models.ForeignKey(
  //     State,
  //     on_delete=models.CASCADE,
  //     related_name='rajbanking_from_state'
  // )
  // from_utility = models.ForeignKey(
  //     Entity,
  //     on_delete=models.CASCADE,
  //     related_name='rajbanking_from_utility'
  // )
  // to_state = models.ForeignKey(
  //     State,
  //     on_delete=models.CASCADE,
  //     related_name='rajbanking_to_state'
  // )
  // to_utility = models.ForeignKey(
  //     Entity,
  //     on_delete=models.CASCADE,
  //     related_name='rajbanking_to_utility'
  // )
  // appr_no = models.CharField(max_length=50)
  // time_block = models.ForeignKey(
  //     TimeSlot,
  //     on_delete=models.CASCADE,
  //     related_name='rajbanking_time_block'
  // )
  // mw = models.DecimalField(max_digits=10, decimal_places=2)
  // converted_mw = models.DecimalField(max_digits=10, decimal_places=2)
  // rate_banking_factor = models.DecimalField(max_digits=5, decimal_places=2)
  date: string; // ISO date string (e.g., 'YYYY-MM-DD')
  time_block: number; // Time block number
  mw: number;
  mwh: number;
}

export interface RajProcurementData {
  collective: RajasthanCollective[];
  compare: CompareRajasthan[];
  tam: RajTamData[];
  deep: RajProcurement[];
  bankingData: RajBankingData[];
}

export interface RajashtanRrvpn {
  date: string;
  drawal_mw: number;
  slot_id: number;
  dateIndex: string;
}
export interface GssLoadDailyDiscomItem {
  date: string;
  discom: string;
  avg_daily_load_mw: number | string;
  daily_peak_load_mw: number | string;
  formatted_data: Array<{
    time_period: string;
    max_load: number | string;
    min_load: number | string;
    avg_load: number | string;
    max_load_slot: number;
    min_load_slot: number;
  }>;
}

// Define the interface for the API response
export interface GssLoadDailyDiscomResponse {
  success: boolean;
  message: string;
  min_date: string;
  max_date: string;
  count: number;
  data: GssLoadDailyDiscomItem[];
}